/**
 * BillingPausedBanner — banner-center source (2026-10-11, no grace — Miles).
 *
 * A paid plan whose payment did not go through pauses new work at once (functions/src/billing/billingPause.ts). The
 * people who can fix it — the owner and anyone holding billing.manage — see it on every page, with one button to the
 * subscription page, where "retry payment" and the card live. Everyone else meets the pause in chat when they ask
 * for work, with who to tell (the server's member-aware refusal), so this band never nags someone who cannot act.
 *
 * Not dismissible: while it shows, no work starts. It comes down by itself the moment a payment succeeds — the server
 * clears accounts/{id}/wallet/displaySummary.billing_pause in the same transaction that lifts the pause.
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useMaybeWalletSummary } from '../../context/WalletSummaryContext';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import { useBannerSurface } from './bannerSurface';
import { useRegisterBanner, type BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';

// copy: billing_paused_band
const BILLING_PAUSE_BAND_COPY = {
  band: 'a payment for your plan didn’t go through, so new work is paused until it’s paid.',
  bandShort: 'plan payment didn’t go through — work is paused.',
  bandAction: 'fix payment',
};

const FixButton = styled.button`
  flex: none;
  min-height: 28px;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #dc2626;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

export const BillingPausedBanner: React.FC = () => {
  const onAppSurface = useBannerSurface();
  const wallet = useMaybeWalletSummary();
  const vis = useUsageVisibility();
  const navigate = useNavigate();

  const paused = wallet?.billingPause?.paused === true;
  const show = onAppSurface && paused && !vis.loading && vis.canManageBilling;

  const fixPayment = () => navigate('/subscription');
  const render = (line: string) => (
    <>
      <span data-billing-paused-banner="">{line}</span>
      <FixButton type="button" onClick={fixPayment}>
        {BILLING_PAUSE_BAND_COPY.bandAction}
      </FixButton>
    </>
  );
  // The same words and action for the new design's band (./bannerNotice.ts).
  const notice: BannerNotice = {
    words: BILLING_PAUSE_BAND_COPY.band,
    shortWords: BILLING_PAUSE_BAND_COPY.bandShort,
    wordsMarker: 'data-billing-paused-banner',
    action: { label: BILLING_PAUSE_BAND_COPY.bandAction, onPress: fixPayment },
  };

  const banner: BannerDescriptor | null = show
    ? {
        id: 'billing-paused',
        tone: 'critical',
        dismissible: false,
        notice,
        content: render(BILLING_PAUSE_BAND_COPY.band),
        shortContent: render(BILLING_PAUSE_BAND_COPY.bandShort),
      }
    : null;

  useRegisterBanner(banner);
  return null;
};

export default BillingPausedBanner;
